import { FCWithChildren } from '@types';
import React from 'react';
import { useHistory } from 'react-router-dom';
import * as yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';
import { Button, Modal, ModalBody, Input, Label, HelperText } from '@components';
import { useForm } from 'react-hook-form';
import useLayoutContext from '@hooks/useLayoutContext';
// @ts-ignore
import BoldynEuaPdf from '@assets/documents/boldyn-end-user-agreement.pdf';
import { IGuestFormData } from '@pages/GuestVisitorsConfirm/GuestVisitorsConfirm';

type ModalGuestAccountProps = {
  deviceConflict?: boolean;
  error?: any;
  loading?: boolean;
  invalidEmail?: boolean;
  isOpen: boolean;
  onClose: () => void;
  onConfirm: (formData: IGuestFormData) => void;
};

const ModalGuestAccount: FCWithChildren<ModalGuestAccountProps> = ({
  deviceConflict,
  error,
  invalidEmail,
  isOpen,
  loading,
  onClose,
  onConfirm,
}) => {
  const history = useHistory();
  const { portalAccessUrl, isCaptivePortal, guestValidationEnabled } = useLayoutContext();
  const GUEST_SCHEMA = yup.object().shape({
    email: guestValidationEnabled
      ? yup.string().email('Must be a valid Email Address').required('Email Address required')
      : yup.string().notRequired(),
    firstName: guestValidationEnabled
      ? yup.string().required('First Name required').min(2)
      : yup.string().notRequired(),
    lastName: guestValidationEnabled
      ? yup.string().required('Last Name required').min(2)
      : yup.string().notRequired(),
    termsAndConditions: yup.boolean().oneOf([true], 'You must accept the Terms and Conditions'),
  });

  const {
    formState: { errors },
    handleSubmit,
    register,
    setValue,
  } = useForm<IGuestFormData>({
    mode: 'onChange',
    reValidateMode: 'onChange',
    resolver: yupResolver(GUEST_SCHEMA),
  });

  const redirectToLogin = () => {
    return history.push(portalAccessUrl(!isCaptivePortal ? '/' : '/login'));
  };
  const isValid = (input: string, isController: boolean = false): boolean =>
    isController ? !(errors as any)[input]?.value?.message : !(errors as any)[input]?.message;

  return (
    <Modal closeModal={onClose} isOpen={isOpen} closeOnEscape={false}>
      <form>
        <ModalBody>
          <div className="mb-6">
            <h1 className="text-3xl antialiased font-normal">Guest Account</h1>
            <div className="text-sm antialiased font-thin">
              Already have an account?{' '}
              <Button layout="link" size="noPadding" onClick={() => redirectToLogin()}>
                Sign in
              </Button>
            </div>
          </div>
          {guestValidationEnabled && (
            <>
              <Label className="my-4">
                <span>
                  Email Address<span className="text-red-600">*</span>
                </span>
                <Input
                  className="my-2"
                  {...register('email')}
                  placeholder="Email Address"
                  type="email"
                  onChange={(e) =>
                    setValue('email', e.target.value.toLowerCase(), { shouldValidate: true })
                  }
                  {...(isValid('email') ? {} : { valid: false })}
                />
                <HelperText valid={isValid('email')}>{errors.email?.message}</HelperText>
              </Label>
              <Label className="my-4">
                <span>
                  First Name<span className="text-red-600">*</span>
                </span>
                <Input
                  className="my-2"
                  {...register('firstName')}
                  placeholder="First Name"
                  type="text"
                  {...(isValid('firstName') ? {} : { valid: false })}
                />
                <HelperText valid={isValid('firstName')}>{errors.firstName?.message}</HelperText>
              </Label>
              <Label className="my-4">
                <span>
                  Last Name<span className="text-red-600">*</span>
                </span>
                <Input
                  className="my-2"
                  {...register('lastName')}
                  placeholder="Last Name"
                  type="text"
                  {...(isValid('lastName') ? {} : { valid: false })}
                />
                <HelperText valid={isValid('lastName')}>{errors.lastName?.message}</HelperText>
              </Label>
            </>
          )}
          <Label check>
            <Input
              {...register('termsAndConditions')}
              type="checkbox"
              {...(isValid('termsAndConditions') ? {} : { valid: false })}
            />
            <p className="px-2 text-xs-left">
              Check box to indicate you agree to the{' '}
              <a
                href={BoldynEuaPdf}
                target="_blank"
                rel="noreferrer noopener"
                className="underline border-2 border-transparent focus:border-blue-700"
              >
                Terms and Conditions
              </a>
            </p>
          </Label>
          <HelperText valid={isValid('termsAndConditions')} className="flex">
            {errors.termsAndConditions?.message}
          </HelperText>
          {!!error && (
            <HelperText valid={false} className="flex">
              {invalidEmail
                ? 'The email you provided was invalid.'
                : deviceConflict
                ? 'This device is already registered, please go back and click Forgot Username or Password if needed.'
                : 'There was a problem creating a temporary guest account. Please try again.'}
            </HelperText>
          )}
          <div className="flex flex-col w-full mt-5 divide-y">
            <Button loading={loading} onClick={handleSubmit(onConfirm)}>
              Create Guest Account
            </Button>
          </div>
        </ModalBody>
      </form>
    </Modal>
  );
};

export default ModalGuestAccount;
